/**
 * Feb 2026 — React Query foundation (Phase 1).
 *
 * Central QueryClient + localStorage-backed persister so the PWA feels
 * native on back-nav and cold relaunch. Everything about our existing
 * axios calls stays untouched — queries just wrap them in a cache.
 *
 * Defaults chosen per operator/customer feedback in the caching plan:
 *   - staleTime: 30s   → back-nav within 30s = 0-fetch instant paint.
 *   - gcTime:   10min  → keep unmounted data around for tab switching.
 *   - refetchOnWindowFocus: true → freshen when the tab regains focus
 *     (e.g. driver PWA returning from Maps).
 *   - retry: 1 → transient hiccups don't blank the screen, real failures
 *     surface fast (avoids the default 3-retry stall).
 *
 * Persistence:
 *   - localStorage key `ash_merch_rq_cache_v1` (bump v-suffix if we ever
 *     need to force-invalidate old cache shape).
 *   - maxAge 24h → beyond that we start fresh; anything older is stale
 *     enough that a fresh fetch is preferable to a possibly-wrong paint.
 */
import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

export const QUERY_CACHE_KEY = 'ash_merch_rq_cache_v1';
export const CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24h

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Create the sync-storage persister (localStorage). Isolated in a factory
 * so tests / SSR can swap it. Falls back to a noop persister when
 * `window` is unavailable (SSR guard).
 */
export function makePersister() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  return createSyncStoragePersister({
    storage: window.localStorage,
    key: QUERY_CACHE_KEY,
    // Throttle writes to 500ms so rapid nav doesn't hammer localStorage.
    throttleTime: 500,
  });
}
